/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import InfoIcon from '@mui/icons-material/esm/InfoOutlined';
import { ClickAwayListener, Tooltip } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid';
import Input from '@mui/material/Input';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
/* eslint-disable react/prop-types, react/forbid-prop-types */
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Actions from '../../../actions/actions';
import { graphManager } from '../../../GraphManager';
import { date } from '../../../utils';
import DialogButton from '../../utils/DialogButton';
import { useJSGNotification } from '../../utils/JSGHooks';
import SettingsPaper from '../../utils/SettingsPaper';
import { setSidePanelHashString } from '../../utils/SidePanel';
import { StreamSheetAccordion } from '../../utils/StreamSheetAccordion';
import { InboxConnectionSettings } from '../connections';

const triggerTypes = [
	{
		label: <FormattedMessage id="InboxSettings.OnInterval" defaultMessage="On Interval" />,
		value: 'interval'
	},
	{
		label: <FormattedMessage id="InboxSettings.OnDataArrival" defaultMessage="On Data Arrival" />,
		value: 'arrival'
	},
	{
		label: <FormattedMessage id="InboxSettings.OnExecute" defaultMessage="On Execute" />,
		value: 'execute'
	},
	{
		label: <FormattedMessage id="InboxSettings.OnMachineStart" defaultMessage="On Machine Start" />,
		value: 'start'
	},
	{
		label: <FormattedMessage id="InboxSettings.OnMachineStop" defaultMessage="On Machine Stop" />,
		value: 'stop'
	}
];

const MINUTE = 60 * 1000;

const localNow = () => Date.now() - new Date().getTimezoneOffset() * MINUTE;
const getLocalDateStringFromDelay = (ms) => {
	if (ms) {
		const delay = new Date(localNow() + ms).toISOString();
		return delay.substring(0, delay.lastIndexOf(':'));
	}
	return undefined;
};
const getDelayFromLocalDateString = (datestr) => {
	const ms = Date.parse(datestr);
	return ms == null || isNaN(ms) ? undefined : Math.max(0, ms - Date.now()); // localNow());
};

const getDefaultIntervalAsString = () => {
	const machine = graphManager.machineContainer;
	const interval = machine ? machine.defaultInterval : 0;
	return date.intervalAsString(interval);
};

const getInitialExpandState = (sheetModel) => {
	// initial expand state depends on current settings (DL-5633):
	const properties = sheetModel.properties;
	const { trigger, loop } = properties;
	if (properties.hideMessages || loop.enabled || trigger.repeat === 'endless') return true;
	if (trigger.type === 'interval') return !!trigger.delay;
	return false;
};

const createLabelWithInfo = ({ labelId, labelDefault, labelValues, infoId, infoDefault, infoValues }) => {
	return (
		<div style={{ display: 'flex', alignItems: 'center' }}>
			<FormattedMessage id={labelId} defaultMessage={labelDefault} values={labelValues} />
			<Tooltip
				enterDelay={300}
				title={<FormattedMessage id={infoId} defaultMessage={infoDefault} values={infoValues} />}
			>
				<InfoIcon
					style={{
						marginLeft: '10px',
						fill: '#888888',
						fontSize: '1rem',
						paddingBottom: '2px'
					}}
				/>
			</Tooltip>
		</div>
	);
};
const wrapWithClickTooltip = ({ control, defaultMessage, messageId, onCloseTooltip, showTooltip }) => {
	return (
		<ClickAwayListener onClickAway={onCloseTooltip}>
			<Tooltip
				PopperProps={{
					disablePortal: true
				}}
				interactive
				open={showTooltip}
				onClick={onCloseTooltip}
				placement="top"
				disableFocusListener
				disableHoverListener
				disableTouchListener
				title={<FormattedMessage id={messageId} defaultMessage={defaultMessage} />}
			>
				{control}
				{/* <ClickAwayListener onClickAway={onCloseTooltip}>{control}</ClickAwayListener> */}
			</Tooltip>
		</ClickAwayListener>
	);
};

function InboxSettingsComponent(props) {
	const { sheetId } = props;
	const sheetModel = sheetId ? graphManager.graph.streamSheetsContainer.find(sheetId) : undefined;
	const [delay, setDelay] = useState(
		sheetModel ? getLocalDateStringFromDelay(sheetModel.properties.trigger.delay) : undefined
	);
	const [loopPath, setLoopPath] = useState(sheetModel ? sheetModel.properties.loop.path : undefined);
	const [expanded, setExpanded] = useState({});
	const [interval, setInterval] = useState(sheetModel.properties.trigger.interval);
	const renderOnNotification = useState(null)[1];
	const [tabSelected, setTabSelected] = useState('settings');
	const [showIntervalTooltip, setShowIntervalTooltip] = useState(false);

	const handleClose = () => {
		props.close();
	};

	const onSettingsNotification = (/* notification */) => {
		// check loop path
		setLoopPath(sheetModel.properties.loop.path);
		renderOnNotification({});
	};
	useJSGNotification('streamsheet_settings_notification', onSettingsNotification);

	const handleSetting = (setting) => {
		props.saveProcessSettings({
			machineId: graphManager.graph.machineContainer.machineId,
			streamsheetId: sheetId,
			...setting
		});
	};

	const handleTriggerChange = (valobj) => {
		handleSetting({ trigger: { ...sheetModel.properties.trigger, ...valobj } });
	};

	const validateLoopPath = (newPath) => {
		// any validations?
		setLoopPath(newPath);
		return newPath;
	};
	const handleLoopPathChange = (event) => validateLoopPath(event.target.value);
	const applyLoopPath = (event) => {
		const path = validateLoopPath(event.target.value);
		handleSetting({ loop: { path } });
	};
	const handleLoopEnabled = (event, state) => {
		const enabled = state;
		// if (!enabled) setLoopPathError(null);
		handleSetting({ loop: { enabled } });
	};
	const handleLoopRecursively = (event, state) => {
		const recursively = state;
		handleSetting({ loop: { recursively } });
	};

	const handleDelayChange = (event) => {
		setDelay(event.target.value);
		// const delay = getDelayFromLocalDateString(event.target.value);
		// handleTriggerChange({ delay });
	};
	const applyDelay = (event) => {
		const ms = getDelayFromLocalDateString(event.target.value);
		handleTriggerChange({ delay: ms });
	};
	const enableDelay = (event, state) => {
		const ms = state ? 60 * MINUTE : undefined;
		setDelay(getLocalDateStringFromDelay(ms));
		handleTriggerChange({ delay: ms });
		setShowIntervalTooltip(true);
	};

	const handleHideMessages = (event, state) => {
		const hideMessages = state;
		handleSetting({ settings: { hideMessages } });
	};

	const handleShowInbox = (event, state) => {
		const showInbox = state;
		handleSetting({ settings: { showInbox } });
	};

	const onExpand = (event, state) => {
		setExpanded({ ...expanded, [sheetId]: state });
	};

	const handleIntervalChange = (event) => {
		let newInterval = event.target.value;
		if (newInterval) {
			newInterval = Number(newInterval);
			if (newInterval !== 0) newInterval = Math.max(1, newInterval);
		}
		setInterval(newInterval);
	};
	const applyInterval = (event) => {
		const newInterval = Math.max(1, Number(event.target.value || 1));
		setInterval(newInterval);
		handleTriggerChange({ interval: newInterval });
	};

	const handleOpenNewConnection = () => setSidePanelHashString('/Connections/new');

	const handleCloseIntervalTooltip = (ev) => {
		if (showIntervalTooltip) {
			ev.preventDefault();
			setShowIntervalTooltip(false);
		}
	};

	if (!props.open || !sheetModel) {
		return null;
	}

	if (expanded[sheetId] == null) expanded[sheetId] = getInitialExpandState(sheetModel);

	return (
		<SettingsPaper
			open={props.open}
			close={handleClose}
			helpContext="inboxsettings"
			title={<FormattedMessage id="InboxSettings.Title" defaultMessage="Inbox Settings" />}
		>
			<Tabs
				sx={{ minHeight: '30px', marginBottom: '14px' }}
				value={tabSelected}
				variant="fullWidth"
				onChange={(event, value) => setTabSelected(value)}
			>
				<Tab
					value="settings"
					sx={{ minHeight: '24px' }}
					label={<FormattedMessage id="InboxSettings.Settings" defaultMessage="Settings" />}
				/>
				<Tab
					value="connections"
					sx={{ minHeight: '24px' }}
					label={<FormattedMessage id="InboxSettings.Connections" defaultMessage="Connections" />}
				/>
			</Tabs>
			{tabSelected === 'settings' ? (
				<FormGroup>
					<TextField
						select
						key="calcsheet"
						variant="outlined"
						size="small"
						margin="normal"
						fullWidth
						value={sheetModel.properties.trigger.type}
						// endlessly should be selected explicitly so:
						onChange={(event) => handleTriggerChange({ type: event.target.value, repeat: 'once' })}
						label={
							<FormattedMessage
								id="InboxSettings.CalcStreamSheet"
								defaultMessage="Calculate StreamSheet"
							/>
						}
					>
						<MenuItem value="none" key="none">
							<em>
								<FormattedMessage id="InboxSettings.Never" defaultMessage="Never" />
							</em>
						</MenuItem>
						{triggerTypes.map((t) => (
							<MenuItem value={t.value} key={t.value}>
								{t.label}
							</MenuItem>
						))}
					</TextField>
					{sheetModel.properties.trigger.type === 'interval'
						? [
								<FormControlLabel
									key="definterval"
									sx={{ mt: '10px' }}
									// style={{ marginBottom: '8px' }}
									control={
										<Checkbox
											checked={sheetModel.properties.trigger.useDefaultInterval}
											onChange={(event, state) =>
												handleTriggerChange({ useDefaultInterval: state })
											}
										/>
									}
									label={createLabelWithInfo({
										labelId: 'InboxSettings.UseDefaultInterval',
										labelDefault: 'Use Default Interval',
										infoId: 'InboxSettings.UseDefaultIntervalTooltip',
										infoDefault:
											'The default interval is currently {interval} and can be changed within App Settings.',
										infoValues: { interval: getDefaultIntervalAsString() }
									})}
								/>,
								<Grid
									container
									key="triggergrid"
									direction="row"
									spacing={1}
									style={{ paddingLeft: '20px' }}
								>
									<Grid xs={4} item>
										<TextField
											variant="outlined"
											size="small"
											margin="normal"
											fullWidth
											label={
												<FormattedMessage
													id="InboxSettings.Interval"
													defaultMessage="Interval"
												/>
											}
											type="number"
											value={interval}
											InputLabelProps={{ shrink: true }}
											inputProps={{ min: 0, step: 1 }}
											disabled={sheetModel.properties.trigger.useDefaultInterval}
											onBlur={applyInterval}
											onChange={handleIntervalChange}
										/>
									</Grid>
									<Grid xs={8} item>
										<TextField
											variant="outlined"
											size="small"
											margin="normal"
											fullWidth
											select
											value={sheetModel.properties.trigger.intervalUnit || 'ms'}
											// eslint-disable-next-line
											label={<FormattedMessage id="InboxSettings.Unit" defaultMessage="Unit" />}
											disabled={sheetModel.properties.trigger.useDefaultInterval}
											onChange={(event) =>
												handleTriggerChange({ intervalUnit: event.target.value })
											}
											input={
												<Input
													name="processSetting.intervalUnit"
													id="processSetting.intervalUnit"
												/>
											}
										>
											<MenuItem value="ms" key="ms">
												<FormattedMessage id="InboxSettings.Ms" defaultMessage="ms" />
											</MenuItem>
											<MenuItem value="s" key="seconds">
												<FormattedMessage id="InboxSettings.Seconds" defaultMessage="seconds" />
											</MenuItem>
											<MenuItem value="m" key="minutes">
												<FormattedMessage id="InboxSettings.Minutes" defaultMessage="minutes" />
											</MenuItem>
											<MenuItem value="h" key="hours">
												<FormattedMessage id="InboxSettings.Hours" defaultMessage="hours" />
											</MenuItem>
											<MenuItem value="d" key="days">
												<FormattedMessage id="InboxSettings.Days" defaultMessage="days" />
											</MenuItem>
										</TextField>
									</Grid>
								</Grid>
						  ]
						: null}
					<div style={{ marginTop: '10px' }}>
						<StreamSheetAccordion
							id="advancedSettings"
							expanded={expanded[sheetId]}
							onChange={onExpand}
							title={<FormattedMessage id="InboxSettings.Advanced" defaultMessage="Advanced Settings" />}
							// details="Advanced settings affecting calculation behavior."
						>
							<FormGroup>
								{sheetModel.properties.trigger.type === 'interval'
									? [
											<FormControlLabel
												key="delaystart"
												sx={{ mt: '10px' }}
												// style={{ marginBottom: '8px' }}
												control={wrapWithClickTooltip({
													showTooltip: showIntervalTooltip,
													messageId: 'InboxSettings.IntervalHintTooltip',
													defaultMessage: 'Restart App for the setting to take effect.',
													onCloseTooltip: handleCloseIntervalTooltip,
													control: <Checkbox checked={!!delay} onChange={enableDelay} />
												})}
												label={createLabelWithInfo({
													labelId: 'InboxSettings.DelayStart',
													labelDefault: 'Postpone interval start',
													infoId: 'InboxSettings.DelayStartTooltip'
												})}
											/>,
											<Grid
												container
												direction="row"
												key="gridstat"
												spacing={1}
												style={{ paddingLeft: '20px' }}
											>
												<Grid xs={12} item>
													<TextField
														variant="outlined"
														size="small"
														margin="normal"
														fullWidth
														label={
															<FormattedMessage
																id="InboxSettings.StarTimeDate"
																defaultMessage="Start Date"
															/>
														}
														type="datetime-local"
														value={delay}
														defaultValue={undefined}
														InputLabelProps={{ shrink: true }}
														disabled={!delay}
														onChange={handleDelayChange}
														onBlur={applyDelay}
														// onChange={(event) => handleTriggerChange({ start: event.target.value })}
													/>
												</Grid>
											</Grid>
									  ]
									: null}
								<FormControlLabel
									sx={{ mt: '10px' }}
									key="trigger"
									control={
										<Checkbox
											checked={sheetModel.properties.trigger.repeat === 'endless'}
											onChange={(event, state) =>
												handleTriggerChange({ repeat: state ? 'endless' : 'once' })
											}
											disabled={sheetModel.properties.trigger.type === 'none'}
										/>
									}
									// eslint-disable-next-line
									label={<FormattedMessage id="InboxSettings.Repeat" defaultMessage="Repeat" />}
								/>
								<FormControlLabel
									sx={{ mt: '10px' }}
									key="loop"
									control={
										<Checkbox
											checked={sheetModel.properties.loop.enabled}
											onChange={handleLoopEnabled}
										/>
									}
									label={createLabelWithInfo({
										labelId: 'InboxSettings.LoopArrayText',
										labelDefault: 'Loop Array',
										infoId: 'InboxSettings.LoopArrayTextTooltip',
										infoDefault: 'Loop Array'
									})}
								/>
								<Grid
									container
									direction="row"
									key="gridloop"
									spacing={1}
									style={{ paddingLeft: '20px' }}
								>
									<Grid xs={12} item>
										<TextField
											variant="outlined"
											size="small"
											fullWidth
											disabled={!sheetModel.properties.loop.enabled}
											label={
												<FormattedMessage
													id="InboxSettings.LoopArray"
													defaultMessage="Loop Array"
												/>
											}
											margin="normal"
											onBlur={applyLoopPath}
											onChange={handleLoopPathChange}
											value={loopPath}
											// error={!!loopPathError}
											// helperText={loopPathError}
										/>
									</Grid>
									<Grid xs={12} item>
										<FormControlLabel
											key="looprec"
											control={
												<Checkbox
													checked={!!sheetModel.properties.loop.recursively}
													onChange={handleLoopRecursively}
												/>
											}
											label={createLabelWithInfo({
												labelId: 'InboxSettings.LoopRecursively',
												labelDefault: 'Recursively',
												infoId: 'InboxSettings.LoopRecursivelyTooltip',
												infoDefault: 'Tip'
											})}
											disabled={!sheetModel.properties.loop.enabled}
										/>
									</Grid>
								</Grid>
								<FormControlLabel
									margin="normal"
									sx={{ mt: '10px' }}
									key="hidemess"
									control={
										<Checkbox
											checked={sheetModel.properties.hideMessages}
											onChange={handleHideMessages}
										/>
									}
									label={
										<FormattedMessage
											id="InboxSettings.HideMessages"
											defaultMessage="Hide Messages"
										/>
									}
								/>
							</FormGroup>
						</StreamSheetAccordion>
					</div>
					<FormControlLabel
						sx={{ mt: '10px' }}
						control={<Checkbox checked={sheetModel.properties.showInbox} onChange={handleShowInbox} />}
						label={<FormattedMessage id="InboxSettings.ShowInbox" defaultMessage="Show Inbox" />}
					/>
				</FormGroup>
			) : null}
			{tabSelected === 'connections' ? (
				<FormGroup
					style={{
						marginTop: '10px',
						marginBottom: '10px'
					}}
				>
					<InboxConnectionSettings
						key="ibcs"
						targetId={sheetId}
						machineId={graphManager.graph.machineContainer.machineId}
					/>
					<DialogButton key="addcon" sx={{ width: '100%' }} marginV onClick={() => handleOpenNewConnection()}>
						<FormattedMessage id="InboxSettings.CreateConnection" defaultMessage="Connection" />
					</DialogButton>
				</FormGroup>
			) : null}
		</SettingsPaper>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

export const InboxSettings = connect(null, mapDispatchToProps)(InboxSettingsComponent);
